import React, { useState, useEffect, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
    ArrowLeft, 
    Monitor, 
    Hash, 
    Settings, 
    Calendar,
    Shield,
    ShieldCheck,
    ShieldX,
    Trash2,
    Search,
    CheckCircle,
    AlertTriangle,
    User,
    Tag,
    RefreshCw,
    Edit
} from 'lucide-react';
import { Modal, Button } from 'react-bootstrap';

import { getAllRegisteredVendingMachines, registerVendingMachine,
     deleteRegisteredVendingMachine } from '../../actions/vendingMachine';

const ListVM = ({ getAllRegisteredVendingMachines, deleteRegisteredVendingMachine, registeredVMList, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, authorized, unauthorized
    const [isDeleting, setIsDeleting] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteSerialNumber, setDeleteSerialNumber] = useState(null);
    const [deleteMachineName, setDeleteMachineName] = useState('');

    useEffect(() => {
        document.title = 'Registered Vending Machines';
        if(registeredVMList === null){
            getAllRegisteredVendingMachines();
        }
    }, [ registeredVMList ] );

    const handleDeleteClick = (serialNumber, machineName) => {
        setDeleteSerialNumber(serialNumber);
        setDeleteMachineName(machineName);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        setIsDeleting(prev => ({ ...prev, [deleteSerialNumber]: true }));
        try {
            await deleteRegisteredVendingMachine({"serialNumber": deleteSerialNumber});
            setShowDeleteModal(false);
            setDeleteSerialNumber(null);
            setDeleteMachineName('');
        } finally {
            setIsDeleting(prev => ({ ...prev, [deleteSerialNumber]: false }));
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setDeleteSerialNumber(null);
        setDeleteMachineName('');
    };

    const filteredMachines = useMemo(() => {
        if (!registeredVMList || !Array.isArray(registeredVMList)) return [];
        
        let filtered = registeredVMList.filter(obj =>
            obj.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            obj.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            obj.version?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (statusFilter !== 'all') {
            filtered = filtered.filter(obj => 
                statusFilter === 'authorized' ? obj.authorised : !obj.authorised
            );
        }

        return filtered;
    }, [registeredVMList, searchTerm, statusFilter]);

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const getAuthorizationBadge = (authorized) => {
        if (authorized) {
            return (
                <span className="badge bg-success d-flex align-items-center w-fit">
                    <ShieldCheck size={14} className="me-1" />
                    Authorized
                </span>
            );
        }
        return (
            <span className="badge bg-danger d-flex align-items-center w-fit">
                <ShieldX size={14} className="me-1" />
                Unauthorized
            </span>
        );
    };

    const getStats = () => {
        if (!registeredVMList) return { total: 0, authorized: 0, unauthorized: 0 };
        
        const total = registeredVMList.length;
        const authorized = registeredVMList.filter(vm => vm.authorised).length;
        const unauthorized = total - authorized;
        
        return { total, authorized, unauthorized };
    };

    const stats = getStats();

    const populateAllRequest = () => {
        if (!filteredMachines.length) {
            return (
                <tr>
                    <td colSpan="7" className="text-center py-5">
                        <Monitor size={48} className="text-muted mb-3" />
                        <p className="text-muted mb-0">
                            {searchTerm || statusFilter !== 'all' ? 
                                'No machines match your search criteria' : 
                                'No registered vending machines found'
                            }
                        </p>
                    </td>
                </tr>
            );
        }

        return filteredMachines.map((obj, index) => (
            <tr key={index} className="align-middle">
                <td className="fw-bold text-muted">#{index + 1}</td>
                <td className="fw-bold text-primary">
                    <Link 
                        to={`/inventory/v1.2/list-registered-vending-machine/${obj.serialNumber}`}
                        className="text-decoration-none d-flex align-items-center"
                    >
                        <Hash size={16} className="me-2" />
                        {obj.serialNumber}
                    </Link>
                </td>
                <td>
                    <div className="d-flex align-items-center">
                        <User size={16} className="me-2 text-muted" />
                        {obj.name}
                    </div>
                </td>
                <td>
                    <span className="badge bg-info fs-6">
                        <Tag size={12} className="me-1" />
                        v{obj.version}
                    </span>
                </td>
                <td className="text-muted">
                    <Calendar size={16} className="me-2" />
                    {formatDate(obj.time)}
                </td>
                <td>
                    {getAuthorizationBadge(obj.authorised)}
                </td>
                <td>
                    <div className="d-flex gap-2">
                        <Link 
                            to={`/inventory/v1.2/list-registered-vending-machine/${obj.serialNumber}`}
                            className="btn btn-outline-primary btn-sm d-flex align-items-center"
                        >
                            <Edit size={14} className="me-1" />
                            Edit
                        </Link>
                        <button 
                            className="btn btn-danger btn-sm d-flex align-items-center"
                            onClick={() => handleDeleteClick(obj.serialNumber, obj.name)}
                            disabled={isDeleting[obj.serialNumber]}
                        >
                            {isDeleting[obj.serialNumber] ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 size={14} className="me-1" />
                                    Delete
                                </>
                            )}
                        </button>
                    </div>
                </td>
            </tr>
        ));
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <Link className="btn btn-outline-primary d-flex align-items-center mb-2" to="/">
                                    <ArrowLeft size={16} className="me-2" />
                                    Back to Dashboard
                                </Link>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold d-flex align-items-center">
                                    <Monitor size={32} className="me-3 text-primary" />
                                    Registered Vending Machines
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage and monitor all registered vending machines
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                {loading && (
                                    <div className="d-flex align-items-center text-muted">
                                        <RefreshCw size={16} className="me-2 spinner-border spinner-border-sm" />
                                        Loading...
                                    </div>
                                )}
                                <span className="badge bg-primary fs-6">
                                    {filteredMachines.length} Machine{filteredMachines.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="row mb-4">
                    <div className="col-xl-4 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{stats.total}</div>
                                        <div className="small">Total Registered</div>
                                    </div>
                                    <Monitor size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-4 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{stats.authorized}</div>
                                        <div className="small">Authorized</div>
                                    </div>
                                    <ShieldCheck size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-4 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-danger text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{stats.unauthorized}</div>
                                        <div className="small">Unauthorized</div>
                                    </div>
                                    <ShieldX size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filter Section */}
                <div className="row mb-4">
                    <div className="col-lg-8">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search by serial number, name, or version..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <select
                                    className="form-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All Machines</option>
                                    <option value="authorized">Authorized Only</option>
                                    <option value="unauthorized">Unauthorized Only</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Registered Machines Table */}
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-white border-bottom">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Settings size={20} className="me-2 text-primary" />
                                        Registered Machines
                                        {filteredMachines.length !== registeredVMList?.length && (
                                            <span className="badge bg-info ms-2">
                                                {filteredMachines.length} of {registeredVMList?.length}
                                            </span>
                                        )}
                                    </h5>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-success d-flex align-items-center">
                                            <ShieldCheck size={12} className="me-1" />
                                            {stats.authorized} Authorized
                                        </span>
                                        <span className="badge bg-danger d-flex align-items-center">
                                            <ShieldX size={12} className="me-1" />
                                            {stats.unauthorized} Unauthorized
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="border-0 fw-bold">#</th>
                                                <th className="border-0 fw-bold">
                                                    <Hash size={16} className="me-2" />
                                                    Serial Number
                                                </th>
                                                <th className="border-0 fw-bold">
                                                    <User size={16} className="me-2" />
                                                    Name
                                                </th>
                                                <th className="border-0 fw-bold">
                                                    <Tag size={16} className="me-2" />
                                                    Version
                                                </th>
                                                <th className="border-0 fw-bold">
                                                    <Calendar size={16} className="me-2" />
                                                    Registration Date
                                                </th>
                                                <th className="border-0 fw-bold">
                                                    <Shield size={16} className="me-2" />
                                                    Authorization
                                                </th>
                                                <th className="border-0 fw-bold text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {populateAllRequest()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            {filteredMachines.length === 0 && !loading && (
                                <div className="card-footer bg-light text-center">
                                    <small className="text-muted">
                                        {searchTerm || statusFilter !== 'all' ? 
                                            'Try adjusting your search criteria or filters to find more machines.' :
                                            'No machines have been registered yet. Register new machines to see them here.'
                                        }
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={cancelDelete} centered>
                    <Modal.Header closeButton className="bg-danger text-white">
                        <Modal.Title>
                            <AlertTriangle size={20} className="me-2" />
                            Confirm Delete
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center py-4">
                        <div className="mb-3">
                            <Trash2 size={48} className="text-danger mb-3" />
                        </div>
                        <h5 className="mb-3">Are you sure you want to delete this vending machine?</h5>
                        <div className="bg-light p-3 rounded mb-3">
                            <strong>Machine Details:</strong><br />
                            <span className="text-muted">Serial Number:</span> <code>{deleteSerialNumber}</code><br />
                            <span className="text-muted">Name:</span> {deleteMachineName}
                        </div>
                        <p className="text-muted mb-0">
                            This action cannot be undone. The vending machine will be permanently removed from the system.
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button 
                            variant="outline-secondary" 
                            onClick={cancelDelete}
                            disabled={isDeleting[deleteSerialNumber]}
                        >
                            <span className="me-2">✕</span>
                            Cancel
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={confirmDelete}
                            disabled={isDeleting[deleteSerialNumber]}
                        >
                            {isDeleting[deleteSerialNumber] ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 size={16} className="me-2" />
                                    Delete Machine
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </Fragment>
    )
}

ListVM.propTypes = {
    getAllRegisteredVendingMachines: PropTypes.func.isRequired,
    registerVendingMachine: PropTypes.func.isRequired,
    deleteRegisteredVendingMachine: PropTypes.func.isRequired,
    registeredVMList: PropTypes.array,
    loading: PropTypes.bool
}

const mapStateToProps = state => ({
    registeredVMList: state.vendingMachine.registeredVMList,
    loading: state.vendingMachine.loading
});

export default connect( mapStateToProps, { getAllRegisteredVendingMachines,
     registerVendingMachine, deleteRegisteredVendingMachine } )( ListVM );